/**
 * 搜索事件数据增强器
 * 优先级：3
 * 职责：为 search 事件注入 filter_one 和 filter_two
 *
 * ⚠️ 重要：完全按照旧版逻辑，调用 getFilter("搜索结果页") 获取 filter_one 和 filter_two
 */

import Router from "next/router";
import { DataEnricher, EnrichContext } from "./types";

export class SearchEventEnricher implements DataEnricher {
  name = "SearchEventEnricher";
  priority = 5; // 在 SearchEnricher (3) 之后执行，确保空字段不会被覆盖

  shouldEnrich(context: EnrichContext): boolean {
    // 只在 search 事件时执行
    const eventName = context.trackData.event_name || context.trackData.event;
    return eventName === "search";
  }

  enrich(context: EnrichContext): Record<string, any> {
    const { trackData, page } = context;
    const { page_nm } = page;

    // ⚠️ 重要：EnricherManager 会直接修改 context.trackData，所以这里直接修改 trackData 即可
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[SearchEventEnricher] Enriching search event, adding empty fields"
      );
    }

    // ⚠️ 重要：完全按照旧版逻辑，添加空字段避免SDK警告
    // 旧版 searchTracker 中硬编码的空字段：
    // design_category_id: 0,
    // filter_five: [],
    // filter_four: [],
    // filter_id: [],
    // filter_six: [],
    if (trackData.design_category_id === undefined) {
      trackData.design_category_id = 0;
    }
    if (trackData.filter_five === undefined) {
      trackData.filter_five = [];
    }
    if (trackData.filter_four === undefined) {
      trackData.filter_four = [];
    }
    if (trackData.filter_id === undefined) {
      trackData.filter_id = [];
    }
    if (trackData.filter_six === undefined) {
      trackData.filter_six = [];
    }

    // ⚠️ 重要：完全按照旧版逻辑，添加其他可能为空值的字段
    // 这些字段可能从 getSearch() 中获取，即使为空也会被包含
    // 为了与旧版保持一致，如果字段不存在，设置为空值
    const emptyStringFields = [
      "color",
      "format",
      "style",
      "design_category_title",
      "design_title",
      "industry_title",
      "search_picture_type",
      "search_source",
    ];
    emptyStringFields.forEach((field) => {
      if (trackData[field] === undefined) {
        trackData[field] = "";
      }
    });

    const emptyArrayFields = [
      "channel_titles",
      "industry_titles",
      "filter_three",
    ];
    emptyArrayFields.forEach((field) => {
      if (trackData[field] === undefined) {
        trackData[field] = [];
      }
    });

    // ⚠️ 重要：旧版数据中同时存在 fliter_four（拼写错误）和 filter_four（正确拼写）
    // 为了与旧版完全一致，需要同时添加这两个字段
    if (trackData.fliter_four === undefined) {
      trackData.fliter_four = [];
    }

    // 完全按照旧版逻辑：调用 getFilter("搜索结果页") 获取 filter_one 和 filter_two
    const filter = this.getFilter(page_nm);

    // 如果调用方没有传 filter_one，则使用 getFilter 的结果
    // ⚠️ 重要：filter_one 必须是数组格式（Validator 要求）
    if (trackData.filter_one === undefined) {
      // getFilter 返回的 filter_one 可能是数组或空字符串
      if (Array.isArray(filter.filter_one)) {
        trackData.filter_one = filter.filter_one;
      } else if (filter.filter_one === "") {
        trackData.filter_one = [];
      } else {
        // 如果是字符串，转换为数组
        trackData.filter_one = [filter.filter_one];
      }
    } else {
      // 如果调用方已经传了 filter_one，确保它是数组格式
      if (!Array.isArray(trackData.filter_one)) {
        trackData.filter_one = trackData.filter_one
          ? [trackData.filter_one]
          : [];
      }
    }

    // 如果调用方没有传 filter_two，则使用 getFilter 的结果
    // ⚠️ 重要：filter_two 必须是数组格式（Validator 要求）
    if (trackData.filter_two === undefined) {
      // getFilter 返回的 filter_two 可能是数组或空字符串
      if (Array.isArray(filter.filter_two)) {
        trackData.filter_two = filter.filter_two;
      } else if (filter.filter_two === "") {
        trackData.filter_two = [];
      } else {
        // 如果是字符串，转换为数组
        trackData.filter_two = [filter.filter_two];
      }
    } else {
      // 如果调用方已经传了 filter_two，确保它是数组格式
      if (!Array.isArray(trackData.filter_two)) {
        trackData.filter_two = trackData.filter_two
          ? [trackData.filter_two]
          : [];
      }
    }

    return trackData;
  }

  /**
   * 获取 filter 数据（完全按照旧版逻辑）
   * 参考：tracker/collecter/get.js 中的 getFilter 函数
   */
  private getFilter(page_nm: string): {
    filter_one: string[] | "";
    filter_two: string[] | "";
  } {
    // 完全按照旧版逻辑：从 DOM 获取 filter_one 和 filter_two
    let filter_one: string[] = [];
    let filter_two: string[] = [];

    // 搜索结果页（完全按照旧版逻辑）
    if (page_nm === "搜索结果页") {
      try {
        // 完全按照旧版逻辑：使用 Router.router.query
        const { type, category } = Router.router?.query || {};

        switch (type) {
          case "board":
            filter_one.push("画板");
            break;
          case "user":
            filter_one.push("用户");
            break;
          case "pin":
            filter_one.push("采集");
            // 二级筛选项_分类信息（完全按照旧版逻辑）
            try {
              const useAppStore = require("@/context/store").default;
              const categories = useAppStore.getState()?.categories || [];
              const categoryName =
                categories.find((item: any) => item?.id === category)?.name ||
                "全部";
              filter_two.push(categoryName);
            } catch (e) {
              filter_two.push("全部");
            }
            break;
          case "material":
            filter_one.push("素材");
            break;
          case "aigc":
            filter_one.push("aigc");
            break;
          default:
            // 默认采集搜索情况（完全按照旧版逻辑）
            if (
              typeof window !== "undefined" &&
              document.querySelector(".ant-tabs-tab-active")
            ) {
              const activeTab = document.querySelector(".ant-tabs-tab-active");
              if (activeTab?.children[0]) {
                filter_one.push(activeTab.children[0].textContent || "");
              }
              // 二级筛选项_分类信息
              try {
                const useAppStore = require("@/context/store").default;
                const categories = useAppStore.getState()?.categories || [];
                const categoryName =
                  categories.find((item: any) => item?.id === category)?.name ||
                  "全部";
                filter_two.push(categoryName);
              } catch (e) {
                filter_two.push("全部");
              }
            }
            break;
        }
      } catch (error) {
        // 静默失败，不影响主流程
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[SearchEventEnricher] Error getting filter for 搜索结果页:",
            error
          );
        }
      }
    }

    // 返回格式与旧版完全一致
    // 旧版：return { filter_one: filter_one.length ? filter_one : "", filter_two: filter_two.length ? filter_two : "" }
    return {
      filter_one: filter_one.length > 0 ? filter_one : ("" as ""),
      filter_two: filter_two.length > 0 ? filter_two : ("" as ""),
    };
  }
}
